import React, { CSSProperties, useEffect, useState } from 'react';
import { toastMessage } from '@croquiscom-pvt/content-ui';
import styled from '@emotion/styled';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';

import { BundlePurchaseItemList } from '@common/checkout-utils';

import { DeliveryTypeToggle } from '../../components/DeliveryTypeToggle/DeliveryTypeToggle';
import { Layout } from '../../components/Layout';
import { FirstOrderText } from '../../components/NoticeText/FirstOrderText';
import { OrderQuantityText } from '../../components/NoticeText/OrderQuantityText';
import { StoreDealText } from '../../components/NoticeText/StoreDealText';
import { SelectBox } from '../../components/SelectBox/SelectBox';
import { TitledHead, TitledHeadType } from '../../components/TitledHead';
import { useOptionsControl } from '../../hooks/useOptionsControl';
import { useCommonService } from '../../services/useCommonService';
import { useSelectOptionService } from '../../services/useSelectOptionService';
import { ProductItem } from '../../types/option-type';
import { ConfirmContainer } from '../confirmation/ConfirmContainer';
import { ItemCardsContainer } from '../item/ItemCardsContainer';
import { RestockNotificationContainer } from '../notification/RestockNotificationContainer';
import { ShippingDetailContainer } from '../notification/ShippingDetailContainer';
import { AdditionalOptionsContainer } from '../options/AdditionalOptionsContainer';
import { OptionContainer } from '../options/OptionContainer';

interface CommonViewProps {
  isLoggedIn: boolean;
  isCouponUnused?: boolean;
  isEditDisabled?: boolean;
  visibleOptionSelect?: boolean;
  visibleAdditional?: boolean;
  disabledConfirmButton?: boolean;
  isEditable?: boolean;
  disabledPrice?: boolean;
  hiddenCounter?: boolean;
  disabledCounter?: boolean;
  disabledConfirm?: boolean;
  disableOrderQuantity?: boolean;
  disabledAutoSelect?: boolean;
  disabledNotification?: boolean;
  onClose?: () => void;
  onEdit?: () => void;
  onAddCart?: (bundle_purchase_item_list: BundlePurchaseItemList) => void;
  extraContent?: React.ReactNode;
  disabledShipping?: boolean;
}

function delay(ms = 0) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const CommonView: React.FC<React.PropsWithChildren<CommonViewProps>> = ({
  isLoggedIn,
  isEditDisabled,
  isCouponUnused,
  visibleOptionSelect = true,
  visibleAdditional = true,
  disabledPrice,
  hiddenCounter,
  disabledCounter,
  disabledConfirm,
  disabledConfirmButton,
  onClose,
  onAddCart,
  isEditable,
  onEdit,
  extraContent,
  disabledShipping,
  disabledAutoSelect,
  disabledNotification,
  disableOrderQuantity,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const {
    isPreview,
    options: {
      catalog_product: {
        minimum_order_quantity,
        maximum_order_quantity,
        product_price: { first_order_discount },
        store_deal_banner,
      },
    },
    has_store_zigzin_option,
    isPurchaseOnlyOneAtTime,
  } = useSelectOptionService();
  const { isOrderQuantityItemType, isOrderQuantityProductType } = useCommonService();
  const [isSelectedSingleItem, setIsSelectedSingleItem] = useState(false);
  const {
    itemList,
    bogo_required_quantity,
    selectOption,
    options,
    textOptions,
    isNoOption,
    optionMatchedItem,
    has_additional_options,
    displayDeliveryTypeToggleButton,
    isItemsShown,
    isRestockNotiDrawerShown,
    isShowShippingDetail,
    resetAll,
    itemCards,
    addItem,
    setIsShowShippingDetail,
    setIsItemsShown,
    goToOptionByIndex,
    activateRestockNotiDrawer,
    makeSelectedItem,
    checkedOnlyZigzinOption,
    setCheckedOnlyZigzinOption,
    validateRequiredTextOptions,
    is_bogo,
    itemCardTotalCount,
  } = useOptionsControl();

  const displayOrderQuantity = maximum_order_quantity || (isOrderQuantityProductType && minimum_order_quantity);
  const display_first_order_text = Boolean(first_order_discount?.min_required_amount);

  useEffect(() => {
    setTimeout(() => {
      setIsLoaded(true);
    });
  }, []);

  useEffect(() => {
    const updateSelectedOption = (optionMatchedItem: ProductItem[]) => {
      // 선택할 옵션이 존재하지 않을 경우 아이템을 하나 만들어주고, 업데이트 X
      if (isNoOption && itemCards.length > 0) return;

      if (!isEmpty(optionMatchedItem) && optionMatchedItem.length > 0) {
        const is_bogo_matched = optionMatchedItem.length - 1 === bogo_required_quantity;
        if (is_bogo && !is_bogo_matched) {
          return;
        }

        const itemQuantity =
          isOrderQuantityItemType && minimum_order_quantity && minimum_order_quantity > 0 ? minimum_order_quantity : 1;
        const productQuantity =
          isOrderQuantityProductType && minimum_order_quantity && itemCardTotalCount >= minimum_order_quantity
            ? 1
            : minimum_order_quantity;
        const quantity = isOrderQuantityProductType ? productQuantity : itemQuantity;

        return makeSelectedItem(optionMatchedItem, quantity, {
          options,
          text_options: textOptions,
          is_bogo,
        });
      }
    };
    const selectedOption = updateSelectedOption(optionMatchedItem);

    // 스토어딜의 경우 1개만 선택해야 함
    if (selectedOption) {
      if (store_deal_banner && itemCards.length > 0) {
        const toast_style: CSSProperties = {
          position: 'relative',
          bottom: '60px',
          maxWidth: 560,
          margin: '0 auto',
        };

        toastMessage('100원딜 상품은 1개까지만 구매 가능합니다', undefined, { toast_style });
        setIsItemsShown(true);
      } else {
        addItem(selectedOption);
        resetAll();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionMatchedItem]);

  /**
   * 선택할 수 있는 상품이 1개라면, 기본 선택 처리
   */
  useEffect(() => {
    const noNeedForSelection =
      disabledAutoSelect || isSelectedSingleItem || !itemList.length || bogo_required_quantity !== null;
    const isSingleItem = itemList.length === 1 && flatten(textOptions).length === 0;

    if (noNeedForSelection || !isSingleItem || checkedOnlyZigzinOption) return;

    async function selectSingleItem() {
      try {
        const filteredItemList = itemList.filter((o) => o.sales_status !== 'SOLD_OUT');
        if (filteredItemList.length === 0) return;

        const flatOptions = flatten(options);
        const singleItemOptions = compact(
          filteredItemList[0].item_attribute_list.map(({ value_id, name, id }, i) => {
            const valueList = flatOptions[i].value_list;

            return {
              ...valueList.find((o) => o.id === value_id),
              name,
              id,
              value_id,
            };
          }),
        );

        for (const option of singleItemOptions) {
          const { id, is_zigzin, value, name, value_id } = option;

          await delay(); // 순차적으로 선택해야 조합 아이템을 만들 수 있기 때문에 동기식 처리

          selectOption({
            name,
            id,
            value: value ?? '',
            value_id,
            is_zigzin: Boolean(is_zigzin),
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsSelectedSingleItem(true);
      }
    }

    selectSingleItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemList, disabledAutoSelect]);

  const handleSelectAnotherOptions = () => {
    const reset_index = validateRequiredTextOptions(0) ? -1 : 0;

    goToOptionByIndex({
      parent_key: reset_index,
      child_key: reset_index,
    });

    setIsItemsShown(false);
  };

  if (isPreview) {
    return (
      <Layout>
        <OptionContainer />
        <AdditionalOptionsContainer />
      </Layout>
    );
  }

  if (isShowShippingDetail) {
    return (
      <Layout
        header={<TitledHead type={TitledHeadType.SHIPPING_DETAIL} onClickBack={() => setIsShowShippingDetail(false)} />}
      >
        <ShippingDetailContainer />
      </Layout>
    );
  }

  if (isRestockNotiDrawerShown) {
    return (
      <Layout
        header={
          <TitledHead
            type={TitledHeadType.RESTOCK_NOTIFICATION}
            onClickBack={() => {
              activateRestockNotiDrawer({ active: false });
              resetAll();
            }}
          />
        }
      >
        <OptionContainer disabledPrice={disabledPrice} disabledNotification={disabledNotification} />
        <RestockNotificationContainer />
      </Layout>
    );
  }

  if (isItemsShown) {
    return (
      <Layout
        footer={
          !disabledConfirm && (
            <ConfirmContainer
              isLoggedIn={isLoggedIn}
              onClose={onClose}
              onAddCart={onAddCart}
              onEditClick={onEdit}
              isEditable={isEditable}
              isEditDisabled={isEditDisabled}
              isCouponUnused={isCouponUnused}
              disabledButton={disabledConfirmButton}
            />
          )
        }
      >
        {visibleOptionSelect && !isNoOption && (
          <SelectBoxContainer no_bottom_margin={!has_additional_options}>
            <SelectBox name='기본상품 선택' onClick={handleSelectAnotherOptions} />
          </SelectBoxContainer>
        )}
        {visibleAdditional && <AdditionalOptionsContainer disabledPrice={disabledPrice} />}
        <ItemCardsContainer
          disabledShipping={disabledShipping}
          disableRemove={isEditable}
          allowAnyQuantity={isEditable}
          disabledPrice={disabledPrice}
          hiddenCounter={hiddenCounter}
          disabledCounter={disabledCounter}
        />
        {display_first_order_text && <FirstOrderText price={first_order_discount?.min_required_amount ?? 0} />}
        {!disableOrderQuantity && displayOrderQuantity && (
          <OrderQuantityText
            minimum_order_quantity={isOrderQuantityProductType ? minimum_order_quantity : 0}
            maximum_order_quantity={maximum_order_quantity}
            isPurchaseOnlyOneAtTime={isPurchaseOnlyOneAtTime}
          />
        )}
        {!!store_deal_banner?.guide_text && <StoreDealText text={store_deal_banner.guide_text} />}
        {extraContent}
      </Layout>
    );
  }

  return (
    <Layout
      header={
        displayDeliveryTypeToggleButton && (
          <DeliveryTypeToggle
            has_store_zigzin_option={has_store_zigzin_option}
            checked={checkedOnlyZigzinOption}
            onClick={() => setCheckedOnlyZigzinOption(!checkedOnlyZigzinOption)}
          />
        )
      }
    >
      {visibleOptionSelect && (
        <OptionContainer
          disabledAnimation={!isLoaded}
          disabledCouponPrice={isCouponUnused}
          disabledPrice={disabledPrice}
          disabledNotification={disabledNotification}
        />
      )}
      {display_first_order_text && <FirstOrderText price={first_order_discount?.min_required_amount ?? 0} />}
      {visibleAdditional && (
        <AdditionalOptionsContainer
          unlimitAdditionalItems={isEditable && visibleAdditional}
          disabledPrice={disabledPrice}
        />
      )}
      {displayOrderQuantity && (
        <OrderQuantityText
          minimum_order_quantity={isOrderQuantityProductType ? minimum_order_quantity : 0}
          maximum_order_quantity={maximum_order_quantity}
          isPurchaseOnlyOneAtTime={isPurchaseOnlyOneAtTime}
        />
      )}
      <RestockNotificationContainer />
    </Layout>
  );
};

const SelectBoxContainer = styled.div<{ no_bottom_margin: boolean }>`
  margin-bottom: ${({ no_bottom_margin }) => (no_bottom_margin ? '-8px' : 0)};
`;
